import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import WhatsAppIcon from '../../assets/whatsapp.svg';

// Add type definition for AnimatePresence
const AnimatePresenceWrapper = AnimatePresence as React.ComponentType<{
  children: React.ReactNode;
  mode?: "wait" | "sync" | "popLayout";
}>;

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  description: string;
  requirements: string[];
  benefits: string[];
  postedAt: string;
  phone: string;
}

interface JobModalProps {
  job: Job | null;
  onClose: () => void;
}

const JobModal: React.FC<JobModalProps> = ({ job, onClose }) => {
  if (!job) return null;

  const handleWhatsAppClick = () => {
    const message = `Olá! Eu vi a vaga de ${job.title} na empresa ${job.company} no app DizQueTem e gostaria de saber mais informações sobre a vaga.`;
    const whatsappUrl = `https://wa.me/${job.phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <AnimatePresenceWrapper mode="wait">
      {job && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-background rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-semibold text-white">
                  {job.title}
                </h2>
                <button
                  onClick={onClose}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-2">
                <p className="text-white/80">
                  <span className="font-medium">Empresa:</span> {job.company}
                </p>
                <p className="text-white/80">
                  <span className="font-medium">Local:</span> {job.location}
                </p>
                <p className="text-white/80">
                  <span className="font-medium">Salário:</span> {job.salary}
                </p>
                <p className="text-white/80">
                  <span className="font-medium">Publicado em:</span> {new Date(job.postedAt).toLocaleDateString('pt-BR')}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-white font-medium">Descrição:</h3>
                <p className="text-white/80 text-sm">
                  {job.description}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-white font-medium">Requisitos:</h3>
                <ul className="list-disc list-inside text-white/80 text-sm">
                  {job.requirements.map((req, i) => (
                    <li key={i}>{req}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="text-white font-medium">Benefícios:</h3>
                <ul className="list-disc list-inside text-white/80 text-sm">
                  {job.benefits.map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              </div>

              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-[#25D366] text-white py-3 rounded-lg hover:bg-[#25D366]/90 transition-colors flex items-center justify-center gap-2"
              >
                <img src={WhatsAppIcon} alt="WhatsApp" className="h-5 w-5" />
                Contato via WhatsApp
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresenceWrapper>
  );
};

export default JobModal; 